// What the right-hand rail SAYS about a research session, one frame at a time
// (2026-09-15 parity audit E-5 / E-9 / E-10 — busy-my-web#487, #490, #491).
//
// The rail used to say one thing for the whole of a search: "Search opened / <query>", from the
// moment the globe was armed until the results were filed. The frames give it four states, and the
// difference between them is the whole point of the column — it is the story being written as it
// happens:
//
//   armed, nothing asked   40006440:182584   "Ready when you are"     / "Search opened"
//   running                40006440:182620   "<query>"                / "Search started"
//   results in             40006440:182640   "<query>" / "Search ready" / "Explore and select what
//                                             you'd like to keep."
//   something ticked       40006448:205688   "<query>" / "N items selected", the kept files listed
//
// Pure, so the wording is testable without mounting the panel.

/** The invitation, before anything has been asked (frame 40006440:182584). */
export const RAIL_INVITATION = { title: 'Search opened', sub: 'Ready when you are', detail: '' }

/** What "Search ready" invites the user to do — the frame's own sentence. */
export const RAIL_READY_DETAIL = "Explore and select what you'd like to keep."

/**
 * The rail row for a live research session.
 *
 * @param {{ query?: string, running?: boolean, selected?: number }} state
 *   query    what was asked (already shortened by lib/searchLabel)
 *   running  the answer has not landed yet
 *   selected how many items are ticked onto the Work-board right now
 * @returns {{ title: string, sub: string, detail: string }}
 */
export function searchRailRow({ query = '', running = false, selected = 0 } = {}) {
  const title = String(query || '').trim() || 'Web search'
  // A SELECTION is the newest thing that has happened, so it replaces "Search ready": the frame
  // draws the count where the ready line was, with the kept files under it and no invitation —
  // the user is plainly already doing what the invitation asks.
  if (selected > 0) {
    return { title, sub: `${selected} item${selected === 1 ? '' : 's'} selected`, detail: '' }
  }
  if (running) return { title, sub: 'Search started', detail: '' }
  return { title, sub: 'Search ready', detail: RAIL_READY_DETAIL }
}

/**
 * The rail's step when the Briefing's "N research packs" filter is opened (frame 40008185:405984,
 * busy-my-web#490): the rail names the filter and says what to do with it. It sits where the next
 * step will land, exactly like the selection node does — a filter is something that has happened
 * to this story.
 *
 * `selecting` is true once at least one pack is ticked: the selection node takes the rail over
 * then (`selectionRailStep`), so this one steps aside rather than repeating the heading above it.
 *
 * `noun` names ONE of them where the plural does not simply lose an "s" (busy-my-web#505: the email
 * connections filter is headed "Email connections" and the frame's sentence is "Select a connection
 * to view it here", not "Select a email connection…"). Omitted, the heading is singularised as
 * before, so every existing caller reads exactly as it did.
 */
export function filterRailStep({ open = false, kindPlural = 'Research packs', selecting = false, noun = '' } = {}) {
  if (!open || selecting) return null
  const one = String(noun || String(kindPlural).replace(/s$/i, '')).toLowerCase()
  return {
    title: kindPlural,
    sub: 'Filter opened',
    detail: `Select a ${one} to view it here`,
  }
}

/**
 * The rail's HEADING while a kind filter is open inside a pack (frame 40007892:377596,
 * busy-my-web#490): "Tasks timeline", with a ✕ that drops the filter — the rail shows only the
 * steps of that kind while it is on.
 *
 * `kind` is the story-filter word the crumb already uses ('task', 'snippet', 'photo', …); the
 * heading is Title-cased and pluralised the way the frame writes it.
 */
const KIND_HEADING = {
  task: 'Tasks', tasks: 'Tasks',
  note: 'Notes', notes: 'Notes', snippet: 'Extractions', snippets: 'Extractions',
  photo: 'Photos', photos: 'Photos',
  link: 'Links', links: 'Links',
  event: 'Events', events: 'Events',
  comparison: 'Comparisons', comparisons: 'Comparisons',
}
export function kindRailTitle(kind) {
  const k = String(kind || '').trim().toLowerCase()
  if (!k) return ''
  const word = KIND_HEADING[k] || `${k.charAt(0).toUpperCase()}${k.slice(1)}${k.endsWith('s') ? '' : 's'}`
  return `${word} timeline`
}
